import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import { useUpdateCommunityProfile } from '~/api/community';
import { useConditions } from '~/api/conditions';
import { useUpdateUser, useUser } from '~/api/user';
import { Separator } from '~/components';
import { bypassBlocker } from '~/constants/router';
import { AnchorTargets, SearchParamKeys } from '~/constants/url';
import { noop } from '~/utils/functions';
import { toast } from '~/utils/toast';

import type { EditUserCommunityProfileFormType } from './CommunitySettings/types';
import type { PersonalInfoFormType } from './PersonalInfo/PersonalInfo.types';

import { FormSwitchField } from '../components/FormSwitchField';
import { SettingsBlocker } from '../components/SettingsBlocker';
import { SettingsSaveAction } from '../components/SettingsSaveAction';
import { SettingsSection, SettingsSections } from '../components/SettingsSection';
import { ChangePassword } from './ChangePassword/ChangePassword';
import { CommunitySettings } from './CommunitySettings/CommunitySettings';
import { mapUserCommunityProfileFormDataToPayload, mapUserCommunityProfileResponseToFormData } from './CommunitySettings/utils';
import { PersonalInfo } from './PersonalInfo/PersonalInfo';

export const Profile = () => {
  const { data: user } = useUser();

  const updateUserMutation = useUpdateUser();
  const updateCommunityVisibilityMutation = useUpdateCommunityProfile(user.communityProfile.id);
  const updateCommunityProfileMutation = useUpdateCommunityProfile(user.communityProfile.id);
  const { refetch: refetchConditions } = useConditions();

  const { t } = useTranslation(['common', 'settings']);

  const navigate = useNavigate();
  const searchParams = useSearchParams()[0];

  const redirectPath = searchParams.get(SearchParamKeys.REDIRECT_PATH);

  const personalInfoForm = useForm<PersonalInfoFormType>({
    defaultValues: {
      firstName: user.firstName,
      lastName: user.lastName,
      lang: user.lang,
      mainOccupation: user.mainOccupation,
      phone: user.phone,
      email: user.email,
    },
  });

  const communityProfileForm = useForm<EditUserCommunityProfileFormType>({
    defaultValues: mapUserCommunityProfileResponseToFormData(user.communityProfile),
  });

  const [isCommunityEnabled, setIsCommunityEnabled] = useState(user.communityProfile.enabled);

  const savePersonalInfo = async (data: PersonalInfoFormType) => {
    const updatedUser = await updateUserMutation.mutateAsync({
      firstName: data.firstName,
      lastName: data.lastName,
      lang: data.lang,
      mainOccupation: data.mainOccupation,
      phone: data.phone,
    });

    if (user.lang !== updatedUser.lang) refetchConditions();

    personalInfoForm.reset({ ...data, lang: updatedUser.lang });
  };

  const saveCommunityProfile = async (data: EditUserCommunityProfileFormType) => {
    const communityProfile = await updateCommunityProfileMutation.mutateAsync(
      mapUserCommunityProfileFormDataToPayload(data, isCommunityEnabled),
    );

    communityProfileForm.reset(mapUserCommunityProfileResponseToFormData(communityProfile));
  };

  const saveCommunityVisibility = async (enabled: boolean) => {
    setIsCommunityEnabled(enabled);

    try {
      await updateCommunityVisibilityMutation.mutateAsync(
        mapUserCommunityProfileFormDataToPayload(mapUserCommunityProfileResponseToFormData(user.communityProfile), enabled),
      );

      // Hidden fields are unmounted, so RHF no longer validates them and their edits must not be saved.
      if (!enabled) communityProfileForm.reset();
    } catch {
      setIsCommunityEnabled(!enabled);
      toast.danger({ description: t('settings:alerts.community.errorUpdated') });
    }
  };

  const isDirty = personalInfoForm.formState.isDirty || communityProfileForm.formState.isDirty;
  const isSaving = updateUserMutation.isPending || updateCommunityProfileMutation.isPending;

  const onSave = async () => {
    const validity = await Promise.all([
      !personalInfoForm.formState.isDirty || personalInfoForm.trigger(),
      !communityProfileForm.formState.isDirty || communityProfileForm.trigger(),
    ]);

    if (validity.includes(false)) {
      // Marks the forms as submitted to trigger RHF native behavior (revalidation mode + focus first error).
      personalInfoForm.handleSubmit(noop)();
      communityProfileForm.handleSubmit(noop)();

      toast.danger({ description: t('settings:alerts.invalidFields') });

      return false;
    }

    const [personalInfo, communityProfile] = await Promise.allSettled([
      personalInfoForm.formState.isDirty && savePersonalInfo(personalInfoForm.getValues()),
      communityProfileForm.formState.isDirty && saveCommunityProfile(communityProfileForm.getValues()),
    ]);

    if (personalInfo.status === 'rejected') {
      toast.danger({ description: t('settings:alerts.errorPersonalInfo') });
    }
    if (communityProfile.status === 'rejected') toast.danger({ description: t('settings:alerts.community.errorUpdated') });

    const isSaved = personalInfo.status === 'fulfilled' && communityProfile.status === 'fulfilled';

    if (isSaved) toast.success({ description: t('settings:alerts.successUpdated') });

    return isSaved;
  };

  const onSaveAndRedirect = async () => {
    const isSaved = await onSave();

    if (isSaved && redirectPath) navigate(redirectPath, { state: bypassBlocker });
  };

  const onDiscard = () => {
    personalInfoForm.reset();
    communityProfileForm.reset();
  };

  return (
    <>
      <SettingsSaveAction isDirty={isDirty} isSaving={isSaving} onSave={onSaveAndRedirect} />

      <SettingsSections>
        <SettingsSection id={AnchorTargets.SETTINGS_PROFILE_INFO} title={t('settings:profile.info.title')}>
          <PersonalInfo form={personalInfoForm} />
        </SettingsSection>

        <Separator />

        <SettingsSection
          action={
            !user.communityProfile.banned && (
              <FormSwitchField
                checked={isCommunityEnabled}
                disabled={updateCommunityVisibilityMutation.isPending || updateCommunityProfileMutation.isPending}
                onCheckedChange={saveCommunityVisibility}
              >
                {t('settings:profile.community.visibleInCommunity')}
              </FormSwitchField>
            )
          }
          description={t('settings:profile.community.description')}
          id={AnchorTargets.SETTINGS_PROFILE_COMMUNITY}
          title={t('settings:profile.community.title')}
        >
          <CommunitySettings form={communityProfileForm} isEnabled={isCommunityEnabled} />
        </SettingsSection>

        <Separator />

        <SettingsSection id={AnchorTargets.SETTINGS_PROFILE_PASSWORD} title={t('settings:profile.password.title')}>
          <ChangePassword />
        </SettingsSection>
      </SettingsSections>

      <SettingsBlocker isDirty={isDirty} isSaving={isSaving} onDiscard={onDiscard} onSave={onSave} />
    </>
  );
};
